import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { ExtendedNotificationComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteSelectors, SiteState } from '@next/core-lib/site';
import { UserSelectors, Customer, Location } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { combineLatest, fromEvent, Observable, Subscription } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { UnitOfMeasureSettings } from 'src/app/settings/models/setting.model';

import { CycleOverviewDetailedPanelComponent } from '../../../cycle/components/cycle-overview-detailed-panel/cycle-overview-detailed-panel.component';
import { WDORegistrationComponent } from '../../../cycle/components/wdo-registration/wdo-registration.component';
import { Cycle, Barn, DayOldChick, CycleModuleState } from '../../../cycle/models/cycle.model';
import { CycleService } from '../../../cycle/services/cycle.service';
import * as cycleActions from '../../../cycle/store/actions/cycle.actions';
import {
  getCycleModuleState,
  getCycles,
  getSelectedCycle,
} from '../../../cycle/store/selectors/cycle.selectors';

import { FeedAdviceModuleState } from '../../../feed-advice/models/feed-advice.models';
import { getFeedAdviceModuleState } from '../../../feed-advice/store/feed-advice.selectors';
import { RefValSheetModuleState } from '../../../reference-value-sheet/models/reference-value-sheet.model';
import { getRefValSheetModuleState } from '../../../reference-value-sheet/store/reference-value-sheet.selectors';
import {
  getObservationSwitchSetting,
  getUnitOfMeasureSettings,
} from '../../../settings/store/selectors/setting.selector';
import { OfflineService } from '../../services/offline.service';
import * as actions from '../../store/actions/within-cycle-performance.actions';
import { getObservations } from '../../store/selectors/within-cycle-performance.selectors';

@Component({
  selector: 'app-within-cycle-performance-overview-simple',
  templateUrl: './within-cycle-performance-overview-simple.component.html',
  styleUrls: ['./within-cycle-performance-overview-simple.component.scss'],
  imports: [
    MatToolbar,
    MatToolbarRow,
    NgIf,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    CycleOverviewDetailedPanelComponent,
    MatButton,
    MatIcon,
    ExtendedNotificationComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class WithinCyclePerformanceOverviewSimpleComponent implements OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly cycleService = inject(CycleService);
  private readonly offlineService = inject(OfflineService);

  serviceWorkerRegistration: ServiceWorkerRegistration;

  pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  isLineChartVisible = false;
  isColumnChartVisible = false;

  showCycleStartInFutureWarning = false;
  shouldShowObservationsPanel$ = this.store.select(getObservationSwitchSetting).pipe();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  upsertObservationOfflineObservable$: Observable<any>;
  upsertObservationOfflineObservableSubscription: Subscription;

  customer$: Observable<Customer> = this.store.pipe(select(UserSelectors.selectCustomer));
  location$: Observable<Location> = this.store.pipe(select(UserSelectors.selectLocation));
  onlineEvent$: Observable<Event>;

  cycles$: Observable<Cycle[]> = combineLatest([
    this.store.select(getCycleModuleState).pipe(
      skipWhile((s) => !s.isLoaded),
      take(1),
    ),
    this.store.select(getRefValSheetModuleState).pipe(
      skipWhile((s) => !s.isLoaded),
      take(1),
    ),
    this.store.select(getFeedAdviceModuleState).pipe(
      skipWhile((s) => !s.isLoaded),
      take(1),
    ),
    this.store.select(getCycles),
    this.store.select(getSelectedCycle).pipe(take(1)),
  ]).pipe(
    skipWhile(
      ([cycleModuleState, rvsState, feedAdviceState, _cycles, _selectedCycle]) =>
        !cycleModuleState.isLoaded || !rvsState.isLoaded || !feedAdviceState.isLoaded,
    ),
    map(
      ([_cycleModuleState, _rvsState, _feedAdviceState, cycles, selectedCycle]: [
        CycleModuleState,
        RefValSheetModuleState,
        FeedAdviceModuleState,
        Cycle[],
        Cycle,
      ]) => {
        if (!this.selectedCycle && selectedCycle) {
          this.selectedCycle = selectedCycle;
        }

        const sortedCyclesWithDayOldChicks = cycles
          .filter((c) => c.dayOldChicks?.length > 0)
          .sort((a, b) => (a.cycleNumber < b.cycleNumber ? 1 : -1))
          .map(
            (c) =>
              <Cycle>{
                ...c,
                // Sort arrivals.
                dayOldChicks: c.dayOldChicks
                  ?.slice()
                  .sort((a, b) => (new Date(b.arrivalDate) < new Date(a.arrivalDate) ? 1 : -1)),
              },
          );

        if (!this.selectedCycle && sortedCyclesWithDayOldChicks?.length > 0) {
          this.selectedCycle = sortedCyclesWithDayOldChicks[0];
        } else if (this.selectedCycle) {
          this.selectedCycle = sortedCyclesWithDayOldChicks.find(
            (c) => c.id === this.selectedCycle.id,
          );
        }

        this.setCycleSpecificInformation();

        return sortedCyclesWithDayOldChicks;
      },
    ),
  );

  selectedCycle: Cycle;
  dayOldChicks: DayOldChick[];
  barnOptions: Barn[];
  isMobile$ = this.breakpoint.ltSm$;

  unitOfMeasureSettings$: Observable<UnitOfMeasureSettings> =
    this.store.select(getUnitOfMeasureSettings);
  unitOfMeasureSettings: UnitOfMeasureSettings = undefined;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.customer$
      .pipe(
        withLatestFrom(this.location$),
        take(1),
        skipWhile(
          ([customer, location]) =>
            location === undefined ||
            location === null ||
            customer === undefined ||
            customer === null,
        ),
      )
      .subscribe(([customer, location]) => {
        this.upsertObservationOfflineObservable$ = this.offlineService.getOfflineDataObservable$(
          customer.id,
          location.id,
          'UpsertObservation',
        );
      });

    this.upsertObservationOfflineObservableSubscription =
      this.upsertObservationOfflineObservable$?.subscribe((a) => {
        if (window.navigator.onLine && a?.type === 'RETRIEVE_OFFLINE_DATA') {
          this.store.dispatch(
            actions.upsertObservationsForCycleRequest({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              observations: a?.payload?.map((payload: any) => payload?.payload),
              unitOfMeasureSettings: this.unitOfMeasureSettings,
            }),
          );
          this.calculatePanels();
          this.clearCachedData();
        }
      });

    this.onlineEvent$ = fromEvent(window, 'online');
    this.onlineEvent$.subscribe(() => {
      // handle online mode
      this.getCachedData();
    });

    this.unitOfMeasureSettings$.pipe(take(1)).subscribe((settings) => {
      this.unitOfMeasureSettings = settings;
    });
  }

  ngOnDestroy(): void {
    if (this.upsertObservationOfflineObservableSubscription) {
      this.upsertObservationOfflineObservableSubscription.unsubscribe();
    }
  }

  getCachedData() {
    this.customer$
      .pipe(
        withLatestFrom(this.location$),
        take(1),
        skipWhile(
          ([customer, location]) =>
            location === undefined ||
            location === null ||
            customer === undefined ||
            customer === null,
        ),
      )
      .subscribe(([customer, location]) => {
        this.offlineService.dispatchGetOfflineData(customer.id, location.id, 'UpsertObservation');
      });
  }

  clearCachedData() {
    this.customer$
      .pipe(
        withLatestFrom(this.location$),
        take(1),
        skipWhile(
          ([customer, location]) =>
            location === undefined ||
            location === null ||
            customer === undefined ||
            customer === null,
        ),
      )
      .subscribe(([customer, location]) => {
        this.offlineService.dispatchDeleteOfflineData(
          customer.id,
          location.id,
          'UpsertObservation',
        );
      });
  }

  changeCycleSelection(selection: MatSelectChange) {
    this.dispatchSelectedCycleId(selection.value as number);
  }

  dispatchSelectedCycleId(cycleId: number) {
    this.store.dispatch(cycleActions.setSelectedCycle({ cycleId }));
    this.setCycleSpecificInformation();
  }

  changeBarnSelection(_selection: MatSelectChange) {
    this.calculatePanels();
  }

  private setCycleSpecificInformation() {
    this.showCycleStartInFutureWarning = new Date(this.selectedCycle?.cycleDate) > new Date();

    // Barns setup.
    this.barnOptions = [];

    // Check in case of older cycles that cycles use barns which do not exist anymore.
    this.selectedCycle?.dayOldChicks?.forEach((d) => {
      if (!this.barnOptions.find((b) => b.id === d.barn?.id)) {
        this.barnOptions.push(d.barn);
      }
    });

    this.calculatePanels();
  }

  private calculatePanels() {
    if (this.selectedCycle) {
      this.store.dispatch(
        actions.loadObservationsForCycleRequest({
          cycleId: this.selectedCycle.id,
          unitOfMeasureSettings: this.unitOfMeasureSettings,
        }),
      );

      this.dayOldChicks = this.selectedCycle.dayOldChicks
        .filter((d) => this.barnOptions.includes(d.barn))
        .sort((a, b) => (new Date(b.arrivalDate) < new Date(a.arrivalDate) ? 1 : -1));
    }
  }

  openWeekRegistrations(cycle: Cycle) {
    this.store
      .select(getObservations)
      .pipe(take(1))
      .subscribe((observations) => {
        const weekRegistrations =
          this.cycleService.convertObservationsRegistrationToWeekRegistrations(
            observations,
            this.barnOptions,
          );

        this.dialog
          .fullscreenDialog(
            WDORegistrationComponent,
            {
              barns: this.barnOptions,
              weekRegistrations,
              cycle,
              confirmButton: true,
              confirmButtonText: `${this.locale.translate('save')}`,
              pushHistoryState: false,
              fullscreen: true,
              isDetailedView: false,
              altButton: true,
              altButtonText: this.locale.translate('wdo-registration.next-day'),
              unitOfMeasureSettings: this.unitOfMeasureSettings,
            },
            {
              maxWidth: '100vw',
              maxHeight: '100vh',
              height: '100%',
              width: '100%',
            },
          )
          .pipe(take(1))
          .subscribe(() => {
            if (window.navigator.onLine) {
              this.calculatePanels();
            }
          });
      });
  }
}
